import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useOpenInWindow } from 'use-open-window'
import showMessageModal from '@/components/message-modal'
import Panel from '@/components/panel'
import ENV_VARS from '@/config'
import { showChangePasswordModal } from '@/features/Auth/ChangePasswordModal'
import { get } from '@/ports/api'
import client from '@/ports/api-client'
import { fontSizes, fontWeights, colors } from '@/styles'

const { BACKEND_SERVER } = ENV_VARS

const StyledPanel = styled(Panel)`
  margin: 0 0 50px;
  padding: 0;
  box-shadow: none;
  border: none;
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  padding: 15px 0;
`

const Liner = styled.div`
  width: 100%;
  height: 0;
`

const SubTitle = styled.h2`
  font-size: 20px;
  font-weight: ${fontWeights.bold};
  line-height: 24px;
  color: ${colors.regular};
  letter-spacing: -0.4px;
  margin: 0 0 30px;
`

const Paragraph = styled.p`
  font-size: 14px;
  text-transform: capitalize;
  color: ${colors.mediumGrey};
  margin: 0;

  ${props =>
    props.subtitle &&
    `
    color: ${colors.regular};
    text-transform: initial;
    margin-bottom: 15px;
  `}

  ${props =>
    props.button &&
    `
    text-transform: initial;
  `}

  ${props =>
    props.bold &&
    `
    font-size: ${fontSizes.regular};
    color: ${colors.regular};
    font-weight: ${fontWeights.medium}
  `}
`

const LinkText = styled.p`
  font-size: 16px;
  color: ${colors.link};
  margin: 0;
  cursor: pointer;
`

const Divider = styled.div`
  border-bottom: 1px solid ${colors.grey};
  margin: 0 auto 50px;
  width: 100%;
`

const SocialLoginPasswordDashboard = () => {
  const { t } = useTranslation()
  const [socialConnect, setSocialConnect] = useState({})
  const [showModal, setShowModal] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const socialList = ['facebook', 'apple', 'google']

  const options = {
    centered: true,
    specs: {
      width: 800,
      height: 600
    }
  }

  const [handleWindowOpen, newWindowHandle] = useOpenInWindow(options)

  const socialConnectStatus = async () => {
    const response = await client.post(`/social_login/info`, {
      resources: { social_login_users: ['provider', 'verified_email'] }
    })
    setSocialConnect(response.data)
  }

  const handleDisconnect = async item => {
    const getter = get(`${BACKEND_SERVER}/social_login/${item}/disconnect`)
    const { response, body } = await getter()

    if (!response.ok) {
      return showMessageModal({
        icon: 'warning',
        title: t('b.settings.view.change_social_warning_title', 'Oops!'),
        message: response.body.message || response.statusText
      })
    }

    if (body.message_key === 'msg.account_has_been_disconnected_from_provider') {
      socialConnectStatus()
    }
  }

  useEffect(() => {
    socialConnectStatus()
  }, [])

  useEffect(() => {
    if (showModal) {
      return showMessageModal({
        icon: 'warning',
        title: t('b.settings.view.change_social_warning_title', 'Oops!'),
        message: modalMessage
      })
    }
  }, [showModal, modalMessage, t])

  useEffect(() => {
    window.addEventListener('message', event => {
      window.removeEventListener('message', () => {})
      if (event.data.source_message === 'mb_social_login') {
        if (!newWindowHandle) return
        newWindowHandle.close()

        if (
          event.data.message_key === 'msg.account_has_been_connected_to_provider' ||
          event.data.message_key === 'msg.general_pass_request'
        ) {
          socialConnectStatus()
        } else {
          setModalMessage(event.data.message)
          setShowModal(true)
        }
      }
    })
  })

  return (
    <>
      <StyledPanel hSpace={0} vSpace={30}>
        <SubTitle>{t('b.settings.view.change_social_title.text', 'Social Login')}</SubTitle>
        <Paragraph subtitle>
          {t(
            'b.settings.view.change_social_description.text',
            'When connected, you may log in by Google, Facebook, and Apple accounts as long as these services use the same email as your liv.rent account.'
          )}
        </Paragraph>
        {socialList.map((item, index) => {
          return (
            <Row key={index}>
              <Paragraph bold>{item}</Paragraph>
              {!socialConnect?.social_login_users?.find(object => object.provider === item) ? (
                <>
                  <LinkText
                    onClick={event => {
                      setShowModal(false)
                      handleWindowOpen(event, {
                        url: `${BACKEND_SERVER}/social_login/${item}/connect`
                      })
                    }}
                  >
                    {t('b.settings.view.change_social_connect', 'Connect')}
                  </LinkText>
                  <Liner />
                  <Paragraph button>
                    {t('b.settings.view.change_social_disconnected', 'Not connected')}
                  </Paragraph>
                </>
              ) : (
                <>
                  <LinkText onClick={() => handleDisconnect(item)}>
                    {t('b.settings.view.change_social_disconnect', 'Disconnect')}
                  </LinkText>
                  <Liner />
                  <Paragraph>{t('b.settings.view.change_social_connected', 'Connected')}</Paragraph>
                </>
              )}
            </Row>
          )
        })}
      </StyledPanel>
      <Divider />
      <StyledPanel hSpace={0} vSpace={30}>
        <SubTitle>{t('b.settings.view.change_social_password_title.text', 'Password')}</SubTitle>
        <LinkText onClick={showChangePasswordModal}>
          {t('b.settings.view.change_social_change_my_password', 'Change my password')}
        </LinkText>
      </StyledPanel>
    </>
  )
}

export default SocialLoginPasswordDashboard
